import { useMemo, useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Button, Field, Modal, PlusIcon } from "@fawkes/ui";
import {
  Activity, Check, ChartSpline, Circle, Columns2, Columns3, Donut, Gauge, Grid2x2, GripVertical,
  LayoutGrid, List, Rainbow, Rows3, Search, Tag, X,
} from "lucide-react";

import {
  DIER_COMPONENTEN, COMPONENT_PER_SLEUTEL, MAX_ONDERDELEN,
  type ComponentStatus,
} from "./dierComponenten";
import { DierOnderdeel } from "./DierOnderdeel";
import { type Dichtheid } from "./Gezondheidsring";
import {
  BOUWER_STIJLEN, TEGEL_BREEDTES, magKiezen, toontActie, verplaatsNaar, wissel, wisselActie,
  type SamengesteldeTegel, type TegelBreedte,
} from "./samengesteldeTegels";
import { dichtheidBijAantal, type VormSleutel } from "./tegelOntwerpen";
import {
  Boogmeter,
  Fotokern,
  Rondom, Gesplitst, HalveMeter, Kwadranten, Prijskaart, Radiaal, Satellieten, Statuslijst,
  TweeKolommen, Waardenboog,
} from "./tegelVormen";

/**
 * De tegelbouwer, naar het ontwerp van 01-10-2026.
 *
 * <p>Drie kolommen: links wat er te kiezen valt, in het midden wat je gekozen hebt en in welke volgorde,
 * rechts hoe het eruitkomt. Dat je het voorbeeld ziet terwijl je kiest is het hele punt -- anders maakt
 * iemand tegels om te ontdekken wat er gebeurt.
 *
 * <p><b>Drie stijlen en niet tien vormen.</b> De tegelbibliotheek kent elf ontwerpen met elf vormen; wie
 * zélf een tegel maakt kiest tussen rond, raster en lijst. Meer keuze maakt dit scherm niet rijker maar
 * onbeslisbaar: de andere vormen bestaan omdat een specifiek ontwerp erom vraagt, niet omdat een
 * gebruiker ze uit elkaar kan houden.
 *
 * <p><b>De snelle actie staat per onderdeel.</b> Op een verzorgingstegel wil je wél snel een weging
 * bijschrijven maar niet per ongeluk het voedingsschema overschrijven. De schakelaar onderaan zet ze
 * allemaal uit; die per onderdeel kiest daarbinnen. Geen van beide deelt rechten uit -- zonder
 * schrijfrecht verdwijnt de knop sowieso, en dat staat er ook bij.
 */

/** Waarden waarmee het voorbeeld toont hoe de tegel eruitkomt, zonder gegevens van een dier. */
const VOORBEELD: Record<string, { waarde: string; herkomst?: string; status?: ComponentStatus }> = {
  gewicht: { waarde: "20,6 kg", herkomst: "Vandaag 08:42" },
  voeding: { waarde: "Royal Canin brok", herkomst: "300 gram · 08:00 / 18:00" },
  medicatie: { waarde: "Metacam", herkomst: "18:00" },
  vaccinaties: { waarde: "geldig tot 1 jun 2027", status: "inOrde" },
  ontworming: { waarde: "14 mrt 2026", herkomst: "volgende: 10 okt", status: "aandacht" },
  ontvlooiing: { waarde: "1 feb 2026", herkomst: "volgende: 1 sep", status: "verlopen" },
  allergieen: { waarde: "graspollen, kip", herkomst: "zwelling om de ogen", status: "verlopen" },
  gedrag: { waarde: "schuw bij harde geluiden", herkomst: "uit het gedragslogboek" },
  verzorging: { waarde: "borstelen dagelijks" },
  chipPaspoort: { waarde: "528210000123456", herkomst: "paspoort NL-0042" },
  dierenarts: { waarde: "Dierenkliniek Enschede" },
  dagprijs: { waarde: "€ 18,50 per dag", herkomst: "€ 22,00 in het hoogseizoen" },
  diersoort: { waarde: "Hond" },
  ras: { waarde: "Labrador Retriever" },
  geslacht: { waarde: "Reu", herkomst: "Gecastreerd" },
  leeftijd: { waarde: "6 jaar", herkomst: "geboren 12 mrt 2020" },
  kenmerken: { waarde: "zwart, witte bef" },
  bijzonderheden: { waarde: "trekt aan de lijn" },
};

/** Hoeveel tegels er bij een breedte naast elkaar passen; het voorbeeld en de dichtheid rekenen ermee. */
const NAAST_BIJ_BREEDTE: Record<TegelBreedte, number> = { heel: 1, half: 2, kwart: 4 };


/**
 * De foto in het voorbeeld.
 *
 * <p>Een getekend silhouet en geen echte hond: de bouwer kent geen dier -- de tegel geldt voor elk dier
 * -- en een echte foto zou suggereren dat je iets over dát dier instelt. Als svg in de bron, zodat het
 * voorbeeld geen bestand hoeft op te halen dat er in een testomgeving niet is.
 */
const VOORBEELD_FOTO =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" fill="#e8ede9"/>` +
    `<circle cx="32" cy="25" r="11" fill="#b9c7bd"/><path d="M12 58c0-11 9-18 20-18s20 7 20 18z" fill="#b9c7bd"/></svg>`,
  );

/* Een stijl kiezen gaat op het oog en niet op het woord: "boogmeter" en "waardenboog" zijn pas uit
   elkaar te houden als je de vorm ernaast ziet. Vandaar een plaatje bij elke naam. */
const STIJLICOON: Record<string, typeof Circle> = {
  ring: Donut,
  satellieten: LayoutGrid,
  statuslijst: List,
  halvemeter: Gauge,
  boogmeter: Activity,
  waardenboog: ChartSpline,
  gesplitst: Columns2,
  radiaal: Rainbow,
  tweekolommen: Columns3,
  kwadranten: Grid2x2,
  prijskaart: Tag,
};

/**
 * De vormen die een foto kunnen tonen: alles behalve de boogmeter.
 *
 * <p>Die is bewust alleen de boog met zijn samenvatting -- één blik, niets eromheen -- en een foto
 * erbij zou precies weghalen waar hij voor bedoeld is.
 */
const MET_MIDDEN: VormSleutel[] = [
  "ring", "halvemeter", "satellieten", "statuslijst", "waardenboog", "gesplitst",
  "radiaal", "tweekolommen", "kwadranten", "prijskaart",
];

const NAASTICOON: Record<number, typeof Circle> = { 1: Rows3, 2: Columns2, 4: Grid2x2 };

/**
 * Een schetsje van wat een maat met de tegel doet: hoeveel er in past en hoe groot het staat.
 *
 * <p>Drie woorden met een uitleg eronder laten je nog steeds raden; een plaatje van de indeling niet.
 * Bewust geen echte tegel in het klein -- die is op dit formaat onleesbaar en zou dus alsnog niets
 * zeggen.
 */
function MaatSchets({ maat }: { maat: "compact" | "normaal" | "uitgebreid" }) {
  const regels = maat === "compact" ? 1 : maat === "normaal" ? 2 : 3;
  return (
    <svg className="tb__maatschets" viewBox="0 0 48 32" aria-hidden="true">
      <rect x="0.5" y="0.5" width="47" height="31" rx="4" fill="none" stroke="currentColor" opacity="0.35" />
      {Array.from({ length: regels }, (_, i) => (
        <g key={i}>
          <rect x="6" y={7 + i * 8} width="7" height="5" rx="1.5" fill="currentColor" opacity="0.45" />
          <rect x="17" y={8 + i * 8} width={maat === "uitgebreid" ? 25 : 18} height="3" rx="1.5"
            fill="currentColor" opacity="0.7" />
        </g>
      ))}
    </svg>
  );
}

export function TegelBouwer({
  tegel,
  tariefPerDier,
  onSluit,
  onBewaar,
  onVerwijder,
}: {
  tegel: SamengesteldeTegel;
  /** Rekent deze vestiging per dier? Bepaalt of de dagprijs gekozen mag worden. */
  tariefPerDier: boolean;
  onSluit: () => void;
  onBewaar: (tegel: SamengesteldeTegel) => void;
  /** Leeg bij een nieuwe tegel: er valt dan nog niets weg te halen. */
  onVerwijder?: () => void;
}) {
  const t = useTranslate();
  const [concept, setConcept] = useState<SamengesteldeTegel>(tegel);
  const [zoek, setZoek] = useState("");
  // De breedte is een keuze van de tegel en geen stand van het voorbeeld: wat je hier ziet krijg je op
  // het dierscherm. Een kwart is één kolom, en vier daarvan naast elkaar bepalen de dichtheid.
  const naast = NAAST_BIJ_BREEDTE[concept.breedte];
  const [sleept, setSleept] = useState<string | null>(null);

  const zichtbaar = useMemo(() => {
    const term = zoek.trim().toLowerCase();
    return DIER_COMPONENTEN.filter((def) => {
      if (term === "") {
        return true;
      }
      // Op naam én op de uitleg: wie "trend" zoekt bedoelt het gewicht, en dat woord staat alleen daar.
      return `${t(def.label)} ${t(def.uitleg)}`.toLowerCase().includes(term);
    });
  }, [zoek, t]);

  return (
    <Modal
      open
      size="full"
      title={t("dierTegel.bouwer.titel")}
      subtitle={t("dierTegel.bouwer.uitleg")}
      onClose={onSluit}
      footer={
        <>
          {onVerwijder && (
            <button type="button" className="tb-weg" onClick={onVerwijder}>
              {t("dierTegel.bouwer.verwijderen")}
            </button>
          )}
          <Button variant="secondary" onClick={onSluit}>{t("common.cancel")}</Button>
          <Button onClick={() => onBewaar(concept)} disabled={concept.componenten.length === 0}>
            {t("dierTegel.bouwer.toevoegen")}
          </Button>
        </>
      }
    >
      <div className="tb">
        <section className="tb__kolom" aria-label={t("dierTegel.bouwer.beschikbaar")}>
          <h4><span className="tb__stap" aria-hidden="true">1</span>{t("dierTegel.bouwer.beschikbaar")}</h4>
          <p className="tb__kolomuitleg">{t("dierTegel.bouwer.beschikbaarUitleg")}</p>

          <div className="tb__zoek">
            <Search size={15} aria-hidden="true" />
            <input
              type="search"
              autoComplete="off"
              value={zoek}
              placeholder={t("dierTegel.bouwer.zoek")}
              aria-label={t("dierTegel.bouwer.zoek")}
              data-testid="bouwer-zoek"
              onChange={(e) => setZoek(e.target.value)}
            />
          </div>

          <ul className="tb__beschikbaar">
            {zichtbaar.map((def) => {
              const gekozen = concept.componenten.includes(def.sleutel);
              const oordeel = magKiezen(concept, def.sleutel, tariefPerDier);
              const Icoon = def.icoon;
              const uit = !gekozen && !oordeel.mag;
              return (
                <li key={def.sleutel}>
                  <button
                    type="button"
                    className="tb__beschikbaaritem"
                    data-gekozen={gekozen || undefined}
                    data-uit={uit || undefined}
                    disabled={uit}
                    data-testid={`bouwer-keuze-${def.sleutel}`}
                    aria-pressed={gekozen}
                    onClick={() => setConcept((c) => wissel(c, def.sleutel, tariefPerDier))}
                  >
                    <span className="tb__itemicoon" aria-hidden="true"><Icoon size={16} /></span>
                    <span className="tb__itemtekst">
                      <strong>{t(def.label)}</strong>
                      <span>
                        {uit && oordeel.reden
                          ? t(`dierTegel.bouwer.reden.${oordeel.reden}`)
                          : t(def.uitleg)}
                      </span>
                    </span>
                    {/* Een vakje en geen plusje: je zet onderdelen aan en uit, en een plus suggereert
                        dat er elke klik eentje bijkomt. */}
                    <span className="tb__vinkvak" aria-hidden="true">
                      {gekozen ? <Check size={13} /> : null}
                    </span>
                  </button>
                </li>
              );
            })}
            {zichtbaar.length === 0 && <li className="tb__leeg">{t("dierTegel.bouwer.nietsGevonden")}</li>}
          </ul>
        </section>

        <section className="tb__kolom" aria-label={t("dierTegel.bouwer.opbouw")}>
          <h4><span className="tb__stap" aria-hidden="true">2</span>{t("dierTegel.bouwer.opbouw")}</h4>
          <p className="tb__kolomuitleg">{t("dierTegel.bouwer.opbouwUitleg")}</p>

          <p className="tb__sleepuitleg">
            <span>{t("dierTegel.bouwer.sleep")}</span>
            <span>{t("dierTegel.bouwer.vanMax", { args: [concept.componenten.length, MAX_ONDERDELEN] })}</span>
          </p>

          <ol className="tb__opbouw">
            {concept.componenten.map((sleutel, i) => {
              const def = COMPONENT_PER_SLEUTEL[sleutel];
              const Icoon = def.icoon;
              return (
                <li
                  key={sleutel}
                  className="tb__gekozen"
                  data-testid={`bouwer-volgorde-${sleutel}`}
                  data-sleept={sleept === sleutel || undefined}
                  draggable
                  onDragStart={() => setSleept(sleutel)}
                  onDragEnd={() => setSleept(null)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (sleept) {
                      setConcept((c) => verplaatsNaar(c, sleept, i));
                    }
                    setSleept(null);
                  }}
                >
                  {/* Slepen met de muis, pijltjes voor wie dat niet kan. Alleen slepen zou de volgorde
                      onbereikbaar maken met een toetsenbord. */}
                  <span
                    className="tb__greep"
                    tabIndex={0}
                    role="button"
                    aria-label={t("dierTegel.bouwer.verplaats", { args: [t(def.label)] })}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setConcept((c) => verplaatsNaar(c, sleutel, i - 1));
                      }
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setConcept((c) => verplaatsNaar(c, sleutel, i + 1));
                      }
                    }}
                  >
                    <GripVertical size={15} aria-hidden="true" />
                  </span>
                  <span className="tb__itemicoon" aria-hidden="true"><Icoon size={16} /></span>
                  <span className="tb__itemtekst">
                    <strong>{t(def.label)}</strong>
                    <span>{t(def.uitleg)}</span>
                  </span>
                  <span className="tb__gekozenknoppen">
                    <button
                      type="button"
                      className="tb__weghalen"
                      aria-label={t("dierTegel.bouwer.weghalen", { args: [t(def.label)] })}
                      onClick={() => setConcept((c) => wissel(c, sleutel, tariefPerDier))}
                    >
                      <X size={15} aria-hidden="true" />
                    </button>
                  </span>
                  <label className="tb__actieschakelaar">
                    <input
                      type="checkbox"
                      checked={!concept.actiesUit.includes(sleutel)}
                      data-testid={`bouwer-actie-${sleutel}`}
                      onChange={() => setConcept((c) => wisselActie(c, sleutel))}
                    />
                    {t("dierTegel.bouwer.snelleActieSoort", { args: [t(`dierTegel.actie.${def.actie}`)] })}
                  </label>
                </li>
              );
            })}
            <li
              className="tb__dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (sleept) {
                  setConcept((c) => verplaatsNaar(c, sleept, c.componenten.length - 1));
                }
                setSleept(null);
              }}
            >
              <span className="tb__dropplus" aria-hidden="true"><PlusIcon width={16} height={16} /></span>
              <strong>{t("dierTegel.bouwer.sleepHierheen")}</strong>
              <span>{t("dierTegel.bouwer.grens", { args: [MAX_ONDERDELEN] })}</span>
            </li>
          </ol>

          <div className="tb__blok">
            <h5>{t("dierTegel.bouwer.stijl")}</h5>
            <p className="tb__blokuitleg">{t("dierTegel.bouwer.stijlUitleg")}</p>
            <fieldset className="tb__stijl" aria-label={t("dierTegel.bouwer.stijl")}>
              {BOUWER_STIJLEN.map((s) => {
                const Icoon = STIJLICOON[s.stijl] ?? Circle;
                return (
                  <label key={s.stijl}>
                    <input
                      type="radio"
                      name="tegelstijl"
                      checked={concept.vorm === s.stijl}
                      data-testid={`bouwer-stijl-${s.stijl}`}
                      onChange={() => setConcept((c) => ({ ...c, vorm: s.stijl as VormSleutel }))}
                    />
                    <Icoon size={18} aria-hidden="true" />
                    <span>{t(s.label)}</span>
                  </label>
                );
              })}
            </fieldset>
          </div>

          {/* Automatisch schalen laat het dashboard de maat kiezen uit het aantal tegels. Uit betekent
              dat deze tegel zijn eigen maat houdt, ook als er tien naast elkaar staan -- en dan moet je
              die maat hier kunnen kiezen. */}
          <label className="tb__schakelaar">
            <input
              type="checkbox"
              checked={concept.automatischSchalen}
              data-testid="bouwer-schalen"
              onChange={(e) => setConcept((c) => ({ ...c, automatischSchalen: e.target.checked }))}
            />
            <span>
              <strong>{t("dierTegel.bouwer.schalen")}</strong>
              <span>{t("dierTegel.bouwer.schalenUitleg")}</span>
            </span>
          </label>

          {!concept.automatischSchalen && (
            <fieldset className="tb__maten">
              {(["compact", "normaal", "uitgebreid"] as const).map((maat) => (
                <label key={maat}>
                  <input
                    type="radio"
                    name="tegelmaat"
                    checked={concept.vasteDichtheid === maat}
                    data-testid={`bouwer-maat-${maat}`}
                    onChange={() => setConcept((c) => ({ ...c, vasteDichtheid: maat }))}
                  />
                  <MaatSchets maat={maat} />
                  <strong>{t(`dierTegel.bouwer.maat.${maat}`)}</strong>
                  <span>{t(`dierTegel.bouwer.maat.${maat}Uitleg`)}</span>
                </label>
              ))}
            </fieldset>
          )}
        </section>

        <section className="tb__kolom" aria-label={t("dierTegel.bouwer.voorbeeld")}>
          <h4><span className="tb__stap" aria-hidden="true">3</span>{t("dierTegel.bouwer.voorbeeld")}</h4>
          <p className="tb__kolomuitleg">{t("dierTegel.bouwer.voorbeeldUitleg")}</p>

          {/* Hoeveel tegels er naast elkaar staan bepaalt de dichtheid, net als op het dashboard. Hier
              kun je zien wat dat met jouw tegel doet voordat je hem maakt. */}
          <div className="tb__naast" role="group" aria-label={t("dierTegel.bouwer.naastElkaar")}>
            {TEGEL_BREEDTES.map((breedte) => {
              const n = NAAST_BIJ_BREEDTE[breedte];
              const Icoon = NAASTICOON[n];
              return (
                <button
                  key={breedte}
                  type="button"
                  aria-pressed={concept.breedte === breedte}
                  data-testid={`bouwer-naast-${n}`}
                  onClick={() => setConcept((c) => ({ ...c, breedte }))}
                >
                  <Icoon size={15} aria-hidden="true" />
                  {t(`dierTegel.bouwer.breedte.${breedte}`)}
                </button>
              );
            })}
          </div>

          <div className="tb__doek" data-naast={naast}>
            <Voorbeeld tegel={concept} dichtheid={dichtheidBijAantal(naast)} />
          </div>

          <p className="tb__hint">{t("dierTegel.bouwer.breedteHint")}</p>

          <div className="tb__blok">
            <h5>{t("dierTegel.bouwer.instellingen")}</h5>
            <p className="tb__blokuitleg">{t("dierTegel.bouwer.instellingenUitleg")}</p>
            <label className="tb__schakelaar">
              <input
                type="checkbox"
                checked={concept.toonHerkomst}
                data-testid="bouwer-herkomst"
                onChange={(e) => setConcept((c) => ({ ...c, toonHerkomst: e.target.checked }))}
              />
              <span>
                <strong>{t("dierTegel.bouwer.toonHerkomst")}</strong>
                <span>{t("dierTegel.bouwer.toonHerkomstUitleg")}</span>
              </span>
            </label>
            {/* De boogmeter kan geen foto tonen; daar zou de schakelaar iets beloven wat niet gebeurt. */}
            {MET_MIDDEN.includes(concept.vorm) && (
              <label className="tb__schakelaar">
                <input
                  type="checkbox"
                  checked={concept.fotoInMidden}
                  data-testid="bouwer-foto"
                  onChange={(e) => setConcept((c) => ({ ...c, fotoInMidden: e.target.checked }))}
                />
                <span>
                  <strong>{t("dierTegel.bouwer.fotoInMidden")}</strong>
                  <span>{t("dierTegel.bouwer.fotoInMiddenUitleg")}</span>
                </span>
              </label>
            )}
          </div>

          <div className="tb__blok">
            <h5>{t("dierTegel.bouwer.naam")}</h5>
            <p className="tb__blokuitleg">{t("dierTegel.bouwer.naamUitleg")}</p>
            <Field
              label={t("dierTegel.bouwer.naam")}
              value={concept.naam}
              maxLength={40}
              data-testid="bouwer-naam"
              onChange={(e) => setConcept((c) => ({ ...c, naam: e.target.value }))}
            />
          </div>
        </section>
      </div>

    </Modal>
  );
}

/**
 * Het live voorbeeld: de tegel zoals hij op het dashboard komt te staan.
 *
 * <p>Dezelfde onderdeelopmaak en dezelfde vormen als daar, in hetzelfde kader. De waarden zijn
 * voorbeelden -- de configuratie bevat geen diergegevens en de tegel geldt voor elk dier -- maar
 * realistisch van lengte, want een chipnummer van vijftien cijfers bepaalt of een tegel in een smalle
 * kolom past.
 */
function Voorbeeld({ tegel, dichtheid }: { tegel: SamengesteldeTegel; dichtheid: Dichtheid }) {
  const t = useTranslate();
  const maat: Dichtheid = tegel.automatischSchalen ? dichtheid : tegel.vasteDichtheid;

  if (tegel.componenten.length === 0) {
    return <p className="tb__leeg">{t("dierTegel.bouwer.nogNiets")}</p>;
  }

  const onderdelen = (
    <>
      {tegel.componenten.map((sleutel) => {
        const def = COMPONENT_PER_SLEUTEL[sleutel];
        const vb = VOORBEELD[sleutel];
        const toont = toontActie(tegel, sleutel);
        return (
          <DierOnderdeel
            key={sleutel}
            def={def}
            waarde={vb?.waarde ?? null}
            herkomst={vb?.herkomst}
            status={vb?.status}
            magSchrijven={toont}
            onActie={toont ? () => {} : undefined}
          />
        );
      })}
    </>
  );

  const props = { onderdelen, dichtheid: maat };
  // Dezelfde foto voor elke vorm met een midden, zodat "toon foto" overal hetzelfde doet. De ronde
  // tegel en de halve meter krijgen hem apart mee: daar zit hij binnen de ring.
  const fotoKern = tegel.fotoInMidden && VOORBEELD_FOTO ? <Fotokern foto={VOORBEELD_FOTO} /> : undefined;

  const segmenten = tegel.componenten.map((sleutel) => ({
    sleutel,
    label: t(COMPONENT_PER_SLEUTEL[sleutel].label),
    status: VOORBEELD[sleutel]?.status ?? ("inOrde" as const),
  }));
  const kern = <span className="tb-vb__kern">{t("dierTegel.bouwer.waarde")}</span>;

  const inhoud = (() => {
    switch (tegel.vorm) {
      case "ring":
        // Dezelfde vorm als op het dashboard. Het voorbeeld had hier zijn eigen tekening -- de oude
        // gezondheidsring -- en liep daardoor achter op de tegel die hij hoort te laten zien: wie de
        // ronde stijl koos zag in de bouwer iets anders dan hij kreeg.
        return <Rondom segmenten={segmenten} foto={tegel.fotoInMidden ? VOORBEELD_FOTO : null} {...props} />;
      case "halvemeter":
        return <HalveMeter segmenten={segmenten} foto={tegel.fotoInMidden ? VOORBEELD_FOTO : null} {...props} />;
      case "boogmeter":
        return <Boogmeter segmenten={segmenten} {...props} />;
      case "waardenboog":
        return <Waardenboog kern={fotoKern ?? kern} {...props} />;
      case "gesplitst":
        return <Gesplitst kern={fotoKern ?? kern} {...props} />;
      case "radiaal":
        return <Radiaal kern={fotoKern ?? kern} {...props} />;
      case "tweekolommen":
        return <TweeKolommen kern={fotoKern ?? kern} {...props} />;
      case "kwadranten":
        return <Kwadranten kern={fotoKern} {...props} />;
      case "prijskaart":
        return <Prijskaart kern={fotoKern ?? <span>€ 18,50</span>} {...props} />;
      case "satellieten":
        return <Satellieten kern={fotoKern} {...props} />;
      default:
        return <Statuslijst kern={fotoKern ?? kern} {...props} />;
    }
  })();

  return (
    <section
      className={`pk-tegel tb__nagebouwd${tegel.toonHerkomst ? "" : " dt-geenherkomst"}`}
      aria-label={t("dierTegel.bouwer.voorbeeld")}
    >
      <header className="pk-tegel__kop">
        <h3>{tegel.naam.trim() || t(`dierTegel.vorm.${tegel.vorm}`)}</h3>
      </header>
      {inhoud}
    </section>
  );
}
